/**
 * WISHDROPZ / LUMINA VINE SHOPIFY THEME JAVASCRIPT
 * Full Shopify compatibility: Ajax Cart, Cart Drawer, 1-Click Buy Now,
 * Variant Switching, Product Gallery, Accordions, and Mobile Navigation.
 */

(function () {
  'use strict';

  // Money Formatter supporting all Shopify currency formats
  const Money = {
    format(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || window.Shopify?.money_format || '₹{{amount}}';

      function defaultOption(opt, def) {
        return typeof opt === 'undefined' ? def : opt;
      }

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultOption(precision, 2);
        thousands = defaultOption(thousands, ',');
        decimal = defaultOption(decimal, '.');

        if (isNaN(number) || number == null) return '0';

        number = (number / 100.0).toFixed(precision);
        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsStr = parts[1] ? decimal + parts[1] : '';

        return dollars + centsStr;
      }

      const match = formatString.match(placeholderRegex);
      const placeholder = match ? match[1] : 'amount';

      switch (placeholder) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return formatString.replace(placeholderRegex, value);
    }
  };

  // 1. AJAX Cart Drawer Engine
  const CartDrawer = {
    overlay: null,
    drawer: null,
    itemsContainer: null,
    subtotalEl: null,
    badges: [],
    drawerBadge: null,

    init() {
      this.overlay = document.querySelector('[data-cart-drawer-overlay]');
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.itemsContainer = document.querySelector('[data-cart-drawer-items]');
      this.subtotalEl = document.querySelector('[data-cart-drawer-subtotal]');
      this.badges = document.querySelectorAll('.cart-count-badge');
      this.drawerBadge = document.querySelector('.cart-drawer-badge');

      // Bind close buttons
      document.querySelectorAll('[data-cart-drawer-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Bind open buttons (e.g. Header cart icon)
      document.querySelectorAll('[data-cart-drawer-trigger]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Bind all standard Add-to-Cart forms
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (form && !form.hasAttribute('data-no-ajax')) {
          e.preventDefault();
          this.addToCart(form, false);
        }
      });

      // Bind 1-Click Buy Now buttons
      document.addEventListener('click', (e) => {
        const buyNowBtn = e.target.closest('[data-buy-now]');
        if (buyNowBtn) {
          e.preventDefault();
          const form = buyNowBtn.closest('form') || document.querySelector('#main-product-form');
          if (form) {
            this.addToCart(form, true);
          }
        }
      });

      // Bind drawer quantity change / remove buttons
      if (this.itemsContainer) {
        this.itemsContainer.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-cart-qty-btn]');
          if (btn) {
            const key = btn.dataset.key;
            const action = btn.dataset.action;
            const currentQty = parseInt(btn.dataset.qty, 10);
            const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
            this.changeQuantity(key, newQty);
          }
        });
      }

      // ESC key closes drawer
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    },

    isOpen() {
      return this.drawer && this.drawer.classList.contains('is-active');
    },

    open() {
      if (!this.drawer) return;
      this.refresh();
      this.drawer.classList.add('is-active');
      if (this.overlay) this.overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-active');
      if (this.overlay) this.overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    },

    async addToCart(form, isBuyNow = false) {
      const submitBtn = isBuyNow 
        ? form.querySelector('[data-buy-now]')
        : (form.querySelector('[type="submit"]') || form.querySelector('[data-add-to-cart]') || form.querySelector('.btn-primary') || form.querySelector('.btn-secondary'));
      
      const originalText = submitBtn ? submitBtn.innerHTML : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.style.opacity = '0.75';
      }

      const addUrl = window.Shopify?.routes?.cart_add_url || '/cart/add.js';

      try {
        const formData = new FormData(form);
        const res = await fetch(addUrl, {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (res.ok) {
          if (isBuyNow) {
            // Immediate checkout redirect
            const checkoutUrl = window.Shopify?.routes?.cart_url ? (window.Shopify.routes.cart_url + '/checkout') : '/checkout';
            window.location.href = checkoutUrl;
            return;
          }
          await this.refresh();
          this.open();
        } else {
          const err = await res.json();
          alert(err.description || err.message || 'Could not add to bag. Please try again.');
        }
      } catch (err) {
        console.error('Error adding to cart:', err);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.style.opacity = '1';
          submitBtn.innerHTML = originalText;
        }
      }
    },

    async changeQuantity(key, quantity) {
      const changeUrl = window.Shopify?.routes?.cart_change_url || '/cart/change.js';
      try {
        const res = await fetch(changeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: key, quantity })
        });
        if (res.ok) {
          await this.refresh();
        }
      } catch (err) {
        console.error('Error changing cart quantity:', err);
      }
    },

    async refresh() {
      const cartUrl = window.Shopify?.routes?.cart_url ? (window.Shopify.routes.cart_url + '.js') : '/cart.js';
      try {
        const res = await fetch(cartUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) return;
        const cart = await res.json();
        this.render(cart);
      } catch (err) {
        console.error('Error refreshing cart:', err);
      }
    },

    render(cart) {
      // Update badge counts
      this.badges.forEach((b) => {
        b.textContent = cart.item_count;
        b.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });

      if (this.drawerBadge) {
        this.drawerBadge.textContent = cart.item_count;
      }

      if (!this.itemsContainer) return;

      if (cart.items.length === 0) {
        this.itemsContainer.innerHTML = `
          <div class="cart-empty-state">
            <svg class="cart-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            <p class="cart-empty-title">Your cart is empty</p>
            <p class="cart-empty-text">Add the Lumina Vine lights to experience the magic.</p>
          </div>
        `;
        if (this.subtotalEl) this.subtotalEl.textContent = Money.format(0);
        const checkoutBtn = document.querySelector('[data-cart-drawer-checkout]');
        if (checkoutBtn) checkoutBtn.style.display = 'none';
        return;
      }

      const checkoutBtn = document.querySelector('[data-cart-drawer-checkout]');
      if (checkoutBtn) checkoutBtn.style.display = 'block';

      let html = '';
      cart.items.forEach((item) => {
        const img = item.image ? `<img src="${item.image}" alt="${item.title}" class="cart-drawer-item-img">` : '';
        html += `
          <div class="cart-drawer-item">
            ${img}
            <div class="cart-drawer-item-details">
              <div>
                <h4 class="cart-drawer-item-title">${item.product_title}</h4>
                ${item.variant_title ? `<p style="font-size: 12px; color: var(--color-inksoft); margin-top: 2px;">${item.variant_title}</p>` : ''}
                <p class="cart-drawer-item-price">${Money.format(item.final_line_price)}</p>
              </div>
              <div class="cart-drawer-item-actions">
                <div class="qty-stepper" style="height: 32px;">
                  <button type="button" class="qty-btn" style="width: 30px; height: 30px; font-size: 16px;" data-cart-qty-btn data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}">-</button>
                  <span style="font-size: 13px; font-weight: 600; width: 26px; text-align: center;">${item.quantity}</span>
                  <button type="button" class="qty-btn" style="width: 30px; height: 30px; font-size: 16px;" data-cart-qty-btn data-action="increase" data-key="${item.key}" data-qty="${item.quantity}">+</button>
                </div>
                <button type="button" class="cart-drawer-remove" data-cart-qty-btn data-action="decrease" data-key="${item.key}" data-qty="1">Remove</button>
              </div>
            </div>
          </div>
        `;
      });

      this.itemsContainer.innerHTML = html;
      if (this.subtotalEl) {
        this.subtotalEl.textContent = Money.format(cart.total_price);
      }
    }
  };

  // 2. Product Variants Selector
  const ProductVariants = {
    init() {
      const productSection = document.querySelector('[data-section-type="product"]');
      if (!productSection) return;

      const jsonScript = productSection.querySelector('[data-product-json]');
      if (!jsonScript) return;

      let productData;
      try {
        productData = JSON.parse(jsonScript.textContent);
      } catch (e) {
        return;
      }

      if (!productData || !productData.variants) return;

      const pills = productSection.querySelectorAll('.variant-pill-btn');
      const hiddenSelect = productSection.querySelector('[data-product-select]');
      const priceEl = productSection.querySelector('[data-product-price]');
      const comparePriceEl = productSection.querySelector('[data-product-compare-price]');
      const discountBadgeEl = productSection.querySelector('[data-product-discount-badge]');
      const addToCartBtn = productSection.querySelector('[data-add-to-cart]');
      const addToCartText = productSection.querySelector('[data-add-to-cart-text]');
      const buyNowBtn = productSection.querySelector('[data-buy-now]');

      const getSelectedOptions = () => {
        const selected = [];
        productSection.querySelectorAll('.variant-select-wrapper > div').forEach((group, idx) => {
          const activePill = group.querySelector('.variant-pill-btn.is-selected');
          if (activePill) {
            selected.push(activePill.dataset.optionValue);
          }
        });
        return selected;
      };

      const updateVariant = () => {
        const selectedOptions = getSelectedOptions();
        const matchedVariant = productData.variants.find((v) => {
          return v.options.every((opt, i) => opt === selectedOptions[i]);
        });

        if (!matchedVariant) return;

        // Update hidden input/select
        if (hiddenSelect) {
          hiddenSelect.value = matchedVariant.id;
          hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Update price
        if (priceEl) {
          priceEl.textContent = Money.format(matchedVariant.price);
        }

        // Update compare-at price & discount badge
        if (comparePriceEl && discountBadgeEl) {
          if (matchedVariant.compare_at_price > matchedVariant.price) {
            comparePriceEl.textContent = Money.format(matchedVariant.compare_at_price);
            comparePriceEl.style.display = 'inline';
            const discount = Math.round(((matchedVariant.compare_at_price - matchedVariant.price) / matchedVariant.compare_at_price) * 100);
            discountBadgeEl.textContent = discount + '% OFF';
            discountBadgeEl.style.display = 'inline';
          } else {
            comparePriceEl.style.display = 'none';
            discountBadgeEl.style.display = 'none';
          }
        }

        // Update availability
        if (addToCartBtn) {
          addToCartBtn.disabled = !matchedVariant.available;
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = !matchedVariant.available;
        }
        if (addToCartText) {
          addToCartText.textContent = matchedVariant.available ? 'Add to Cart' : 'Sold Out';
        }

        // Update URL query string
        if (history.replaceState) {
          const newUrl = window.location.protocol + '//' + window.location.host + window.location.pathname + '?variant=' + matchedVariant.id;
          window.history.replaceState({ path: newUrl }, '', newUrl);
        }
      };

      pills.forEach((pill) => {
        pill.addEventListener('click', (e) => {
          e.preventDefault();
          const parentGroup = pill.closest('.variant-options-group');
          parentGroup.querySelectorAll('.variant-pill-btn').forEach((p) => p.classList.remove('is-selected'));
          pill.classList.add('is-selected');

          // Update label text
          const labelSpan = pill.closest('div').parentElement.querySelector('.selected-option-value');
          if (labelSpan) {
            labelSpan.textContent = pill.dataset.optionValue;
          }

          updateVariant();
        });
      });
    }
  };

  // 3. Product Gallery Component
  const ProductGallery = {
    init() {
      const galleries = document.querySelectorAll('[data-product-gallery]');
      galleries.forEach((gallery) => {
        const mainImg = gallery.querySelector('[data-gallery-main-image]');
        const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
        const prevBtn = gallery.querySelector('[data-gallery-prev]');
        const nextBtn = gallery.querySelector('[data-gallery-next]');
        const counterEl = gallery.querySelector('[data-gallery-counter]');
        if (!mainImg || thumbs.length === 0) return;

        let currentIndex = 0;
        const total = thumbs.length;

        const updateGallery = (index) => {
          currentIndex = (index + total) % total;
          const targetThumb = thumbs[currentIndex];
          const newSrc = targetThumb.dataset.fullSrc || targetThumb.querySelector('img').src;
          const newAlt = targetThumb.querySelector('img').alt || '';

          mainImg.style.opacity = '0';
          setTimeout(() => {
            mainImg.src = newSrc;
            mainImg.alt = newAlt;
            mainImg.style.opacity = '1';
          }, 150);

          thumbs.forEach((t, i) => {
            t.classList.toggle('is-active', i === currentIndex);
          });

          if (counterEl) {
            counterEl.textContent = `${currentIndex + 1} / ${total}`;
          }
        };

        thumbs.forEach((thumb, idx) => {
          thumb.addEventListener('click', () => updateGallery(idx));
        });

        if (prevBtn) {
          prevBtn.addEventListener('click', () => updateGallery(currentIndex - 1));
        }
        if (nextBtn) {
          nextBtn.addEventListener('click', () => updateGallery(currentIndex + 1));
        }

        // Touch Swipe
        let startX = 0;
        mainImg.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
        }, { passive: true });

        mainImg.addEventListener('touchend', (e) => {
          const diffX = e.changedTouches[0].clientX - startX;
          if (Math.abs(diffX) > 40) {
            if (diffX < 0) updateGallery(currentIndex + 1);
            else updateGallery(currentIndex - 1);
          }
        }, { passive: true });
      });
    }
  };

  // 4. Interactive Accordions (Collapsible FAQ & Details)
  const Accordions = {
    init() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-accordion-trigger]');
        if (!trigger) return;
        const item = trigger.closest('.accordion-item');
        const content = item.querySelector('.accordion-content');
        if (!item || !content) return;

        const isOpen = item.classList.contains('is-open');

        // Close siblings if in a single-accordion group
        const group = item.closest('[data-accordion-group]');
        if (group) {
          group.querySelectorAll('.accordion-item.is-open').forEach((sibling) => {
            if (sibling !== item) {
              sibling.classList.remove('is-open');
              const sibContent = sibling.querySelector('.accordion-content');
              if (sibContent) sibContent.style.maxHeight = null;
            }
          });
        }

        if (isOpen) {
          item.classList.remove('is-open');
          content.style.maxHeight = null;
        } else {
          item.classList.add('is-open');
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });

      // Expand any item with .is-open initially
      document.querySelectorAll('.accordion-item.is-open .accordion-content').forEach((c) => {
        c.style.maxHeight = c.scrollHeight + 'px';
      });
    }
  };

  // 5. Mobile Navigation Drawer Toggle
  const MobileMenu = {
    init() {
      const toggle = document.querySelector('[data-mobile-menu-toggle]');
      const drawer = document.querySelector('[data-mobile-menu-drawer]');
      if (!toggle || !drawer) return;

      toggle.addEventListener('click', () => {
        const isOpen = drawer.classList.contains('is-open');
        drawer.classList.toggle('is-open', !isOpen);
      });

      drawer.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          drawer.classList.remove('is-open');
        });
      });
    }
  };

  // 6. Quantity Stepper for Product Pages
  const QuantityStepper = {
    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-step]');
        if (!btn) return;
        const container = btn.closest('.qty-stepper');
        const input = container.querySelector('input[name="quantity"]');
        if (!input) return;

        let val = parseInt(input.value, 10) || 1;
        if (btn.dataset.qtyStep === 'minus') {
          val = Math.max(1, val - 1);
        } else {
          val += 1;
        }
        input.value = val;
      });
    }
  };

  // 7. Wishlist Button (Persisted in localStorage)
  const Wishlist = {
    init() {
      const buttons = document.querySelectorAll('[data-wishlist-toggle]');
      const isLiked = localStorage.getItem('wishdropz_wishlist') === 'true';

      buttons.forEach((btn) => {
        btn.classList.toggle('active', isLiked);
        btn.addEventListener('click', () => {
          const current = btn.classList.contains('active');
          const next = !current;
          buttons.forEach((b) => b.classList.toggle('active', next));
          localStorage.setItem('wishdropz_wishlist', next ? 'true' : 'false');
        });
      });
    }
  };

  // Initialize all components on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    CartDrawer.init();
    ProductVariants.init();
    ProductGallery.init();
    Accordions.init();
    MobileMenu.init();
    QuantityStepper.init();
    Wishlist.init();
  });
})();
